import * as yup from "yup";
import { MUNICIPALITY_TAX_INCLUSIVE_STATUSES } from "../types/eventTypes";

export enum USER_LOGIN_FORM_FIELDS {
	EMAIL = "email",
	PASSWORD = "password",
}

export const DEFAULT_USER_LOGIN_FORM_VALUE = {
	[USER_LOGIN_FORM_FIELDS.EMAIL]: "",
	[USER_LOGIN_FORM_FIELDS.PASSWORD]: "",
};

export const USER_LOGIN_SCHEMA = yup.object({
	[USER_LOGIN_FORM_FIELDS.EMAIL]: yup.string().required("Email is invalid").email("Invalid email"),
	[USER_LOGIN_FORM_FIELDS.PASSWORD]: yup.string().required("Password is required"),
});

export const DECLINE_VENDOR_REASONS = [
	{
		FormControlLabelValue: "countTooLow",
		FormControlLabelLabel: "Guest count too low",
	},
	{
		FormControlLabelValue: "budgetTooLow",
		FormControlLabelLabel: "Budget too low",
	},
	{
		FormControlLabelValue: "specifiedDateTime",
		FormControlLabelLabel: "Not available on specified Date/Time",
	},
	{
		FormControlLabelValue: "specifiedServices",
		FormControlLabelLabel: "Unable to provide specified services",
	},
	{
		FormControlLabelValue: "outOfService",
		FormControlLabelLabel: "Event out of service area/city",
	},
	{ FormControlLabelValue: "other", FormControlLabelLabel: "Other" },
];

export const QUOTE_FORM_FINANCIAL_SCHEMA = yup.object({
	amount: yup
		.mixed()
		.test("required", "Amount is required", (value) => {
			return value !== "" && value != null && value !== undefined;
		})
		.transform((value) => {
			if (value === "" || value == null || value === undefined) return value;
			return Number(value);
		})
		.test("min-amount", "Amount should be greater than or equal to 400", (value) => {
			if (value === "" || value == null || value === undefined) return true;
			const numValue = Number(value);
			return !isNaN(numValue) && numValue >= 400;
		})
		.test("max-decimal-places", "Amount cannot have more than 2 numbers after decimal", (value) => {
			if (value === "" || value == null || value === undefined) return true;
			const numValue = Number(value);
			if (isNaN(numValue)) return true;
			const decimalPlaces = numValue.toString().split(".")[1]?.length || 0;
			return decimalPlaces <= 2;
		}),
	municipalityTax: yup
		.mixed()
		.test(
			"conditional-required",
			"Municipality tax cannot be AED 0.00. Enter correct amount if applicable.",
			function (value) {
				const { isMunicipalityTaxInclusive } = this.parent;
				if (isMunicipalityTaxInclusive === MUNICIPALITY_TAX_INCLUSIVE_STATUSES.NotApplicable) {
					return true; // Not required if not applicable
				}
				if (value === "" || value == null || value === undefined) {
					return false; // Required when applicable
				}
				const numValue = Number(value);
				return !isNaN(numValue) && numValue > 0;
			},
		)
		.transform((value) => {
			if (value === "" || value == null || value === undefined) return value;
			return Number(value);
		})
		.test("max-decimal-places", "Municipality tax cannot have more than 2 numbers after decimal", (value) => {
			if (value === "" || value == null || value === undefined) return true;
			const numValue = Number(value);
			if (isNaN(numValue)) return true;
			const decimalPlaces = numValue.toString().split(".")[1]?.length || 0;
			return decimalPlaces <= 2;
		})
		.test(
			"less-than-amount",
			"Municipality tax cannot be greater than or equal to the amount",
			function (value) {
				const { amount } = this.parent;
				if (value === "" || value == null || value === undefined || !amount) return true;
				const numValue = Number(value);
				const amountValue = Number(amount);
				if (isNaN(numValue) || isNaN(amountValue)) return true;
				return numValue < amountValue;
			},
		),
});

export const ATTACHMENT_MAX_SIZE_MB = 50;
export const MAX_ATTACHMENTS = 4;
export const MAX_DOCUMENT_PAGES = 50;

export const PDF_UPLOAD_ACCEPTED_FORMATS = {
	"application/pdf": [],
};

export const IMAGE_UPLOAD_ACCEPTED_FORMATS = {
	"image/png": [],
	"image/jpeg": [],
	"image/jpg": [],
};

export const FILE_TOO_LARGE_ERROR_CODE = "file-too-large";
export const FILE_INVALID_TYPE_ERROR_CODE = "file-invalid-type";
export const FILE_INVALID_TYPE_ERROR_TEXT = "Invalid file type";
export const ATTACHMENT_FILE_TOO_LARGE_ERROR_TEXT = (fileSizeLimit: number) =>
	`Max file size allowed is ${fileSizeLimit}MB`;
export const ATTACHMENT_UPLOAD_LIMIT_REACHED_ERROR_TEXT = (maxAttachments: number) =>
	`Upload limit reached. Only ${maxAttachments} files allowed.`;
export const DOCUMENT_PAGE_LIMIT_REACHED_ERROR_TEXT = (maxPages: number) =>
	`Max page limit allowed per quote session is ${maxPages} pages`;
export const REQUIRED_FIELDS_ERROR_TEXT = "Please fill in all required fields";
export const CORPORATE_QUOTE_CONTENT_ATTACHMENTS_PATH = "corporate/events/quote/content/attachments";
export const CORPORATE_QUOTE_PDF_ATTACHMENTS_PATH = "corporate/events/quote/attachments";
export const SOMETHING_WENT_WRONG_ERROR_TEXT =
	"Something went wrong, please try again later or contact support if the issue persists";
export const NO_MENU_OR_SERVICES_INFO_FOUND_ERROR_TEXT =
	"No menu or catering service information found in the provided PDF.";
export const MENU_AND_SERVICES_INFO_EXTRACTED_SUCCESS_TEXT = "Extraction completed successfully";
export const PHOTO_LIBRARY_PHOTOS_PATH = "vendors/photo-library/:vendorName";
